import React, { useEffect, useState } from "react";
import { Header, Loading, RewardCard, RewardScanner } from "../../components";
import { useStateContext } from "../../contexts/ContextProvider";
import checkin from "../../data/checkin.png";
import {
  collection,
  limit,
  orderBy,
  query,
  doc,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useSelector } from "react-redux";
import { userId } from "../../redux/reducers/userReducer";
import Swal from "sweetalert2";

const Rewards = () => {
  const { activeMenu } = useStateContext();
  const currentUserID = useSelector(userId);
  const [open, setOpen] = useState(false);
  const [reward, setReward] = useState(0);
  const [rewardcard, setRewardcard] = useState([]);
  const [visitedTimes, setVisitedTimes] = useState(null);
  const [scannedAt, setScannedAt] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch the reward cards from the Collection Bitcare_Rewardcards and store in rewardcard state array
  useEffect(() => {
    const fetchRewards = async () => {
      try {
        const collectionRef = collection(db, "/Bitcare_Rewardcards");
        const queryRef = query(collectionRef, limit(5), orderBy("id", "asc"));
        const unsubscribe = onSnapshot(queryRef, (snapshot) => {
          setLoading(true);
          const cards = snapshot.docs.map((doc) => ({
            ...doc.data(),
          }));
          setRewardcard(cards);
        });

        return () => unsubscribe(); // Cleanup the listener on unmount
      } catch (err) {
        console.log(err);
      }
    };
    fetchRewards();
  }, [currentUserID]);

  // Fetch user reward card based on userId and rerender everytime when vistited times is changing
  useEffect(() => {
    const fetchUserRewards = async () => {
      try {
        const docRef = doc(db, "/Bitcare_UserRewards", currentUserID);
        const unsubscribe = onSnapshot(docRef, (docSnapshot) => {
          if (docSnapshot.exists()) {
            setLoading(true);
            const visitNumber = docSnapshot.data().visitedTimes;
            const scannedAt = docSnapshot.data().scannedTime;
            setVisitedTimes(visitNumber);
            setScannedAt(scannedAt);
          }
        });

        return () => unsubscribe();
      } catch (Err) {
        console.log("error fetch document");
      }
    };

    fetchUserRewards();
  }, [visitedTimes]);

  // Function to open a camera to scan the QR code
  const openScanner = () => {
    if (scannedAt === null) {
      setOpen(true);
    }
    const OneDayInMillis = 24 * 60 * 60 * 1000;
    const currentTimestamp = Date.now();
    const scannedTimestamp =
      scannedAt.seconds * 1000 + Math.floor(scannedAt.nanoseconds / 1000000);
    const timeDifference = currentTimestamp - scannedTimestamp;
    if (timeDifference < OneDayInMillis) {
      Swal.fire({
        title: "Error!",
        text: "You can only scan once every 24 hours. Please try again later.",
        icon: "error",
        confirmButtonText: "Close",
        confirmButtonColor: "#116C6E", // Customize button color
      });
    } else {
      setOpen(true);
    }
  };

  return (
    <div className="mx-3 mt-20 md:mt-10">
      <Header title="My Rewards" />
      <div
        className="md:bg-secondary-bg md:p-6 mt-4 rounded-sm text-gray-700  md:mx-5 mb-5   
        grid gap-4   
      "
      >
        <div className="grid gap-4">
          <div className="font-semibold text-md md:text-lg text-center">
            Make sure to Check-In every time you're in our location!
          </div>
          <div className="text-sm font-medium text-center">
            Simply press the Check-In Icon , and scan the QR code with your
            camera!
          </div>
          <div className="flex items-center justify-center">
            <img
              onClick={openScanner}
              src={checkin}
              alt="checkin"
              className="w-36 object-cover rounded-full cursor-pointer"
            />
          </div>
          <div className=" rounded-sm p-2 flex flex-col items-center">
            <div className="font-semibold text-lg text-center p-2">
              Reward Tracker
            </div>
            <div>
              {visitedTimes && (
                <span className="font-bold">
                  Current visit number = {visitedTimes}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* reward cards */}
        <div className="flex justify-center ">
          {loading ? (
            <div
              className={`bg-white flex flex-col w-full xl:w-1/2 justify-center shadow-sm rounded-md
            ${activeMenu ? "" : "lg:w-1/2"}
            `}
            >
              {rewardcard?.map((item) => (
                <RewardCard
                  key={item.docid}
                  id={item.id}
                  toEarn={item.toEarn}
                  visitNumber={item.visitNumber}
                  reward={item.reward}
                  rewardDisclaimer={item.rewardDisclaimer}
                  visitedTimes={visitedTimes}
                />
              ))}
            </div>
          ) : (
            <Loading />
          )}
        </div>
        {open && (
          <RewardScanner
            setOpen={setOpen}
            setVisitedTimes={setVisitedTimes}
            visitedTimes={visitedTimes}
            reward={reward}
            setReward={setReward}
          />
        )}
      </div>
    </div>
  );
};

export default Rewards;

/**
 
**Documentation: Rewards Component

The Rewards component represents the page where users can view and interact with their rewards. 
The page displays information about reward cards, allows users to check-in, 
and provides a reward scanner for scanning QR codes to earn rewards.

**Dependencies:
react: The core library for building user interfaces in React applications.
react-redux: The official React bindings for Redux.
firebase: A backend-as-a-service platform that allows developers to build web and mobile applications without managing servers.
sweetalert2: A JavaScript library for displaying custom alert messages.


**State Variables:
activeMenu: Holds the current state of the active menu.
currentUserID: Holds the user ID obtained from the Redux store.
open: A boolean state variable that determines whether the reward scanner is open or not.
reward: Represents the reward points earned by the user.
rewardcard: An array state variable that stores reward card data fetched from the Firebase collection.
visitedTimes: Holds the number of times the user has visited the location.
scannedAt: Holds the timestamp of the last time the user scanned the QR code.
loading: A boolean state variable that determines whether the rewards data is being loaded or not.

**Hooks:
useEffect: A hook that runs side effects in functional components. It is used to fetch reward card data and user rewards based on the user ID.
useState: A hook that allows adding state to functional components.

**Component Structure:
The component fetches reward card data from the Firebase collection Bitcare_Rewardcards 
and stores it in the rewardcard state array using the useEffect hook.
The component fetches user reward data based on the user ID from the Firebase document Bitcare_UserRewards 
and updates the visitedTimes and scannedAt states using the useEffect hook. 
The visitedTimes state is used to re-render the component whenever the user's visit count changes.
The component includes a function openScanner that determines whether the reward scanner should be opened. 
If the user has not scanned the QR code within the last 24 hours, the scanner will be opened; otherwise, an error alert is displayed using the sweetalert2 library.
The component renders reward information, including the check-in icon, current visit number, and a reward tracker. 
The reward tracker displays the reward cards that the user can earn.
The component renders reward cards in a grid layout, showing details such as the reward points required to earn the reward, t
he number of visits required, and a reward disclaimer. The RewardCard component is used to render each individual reward card.
If the loading state is true, the component renders a loading indicator; otherwise, it renders the reward cards.
If the open state is true, the RewardScanner component is rendered. The RewardScanner component allows the user to scan the QR code to earn rewards.

**Rendered Output:
The component renders a page that displays reward information, including the check-in icon, current visit number, 
and reward tracker. It also renders reward cards in a grid layout, showing details about each reward, 
and a reward scanner for scanning QR codes to earn rewards.
 */
